import React, { useEffect, useState } from 'react';
import { KibSheet } from '@chewy/kib-overlays-react';
import { KibSelectionCard } from '@chewy/kib-cards-react';
import '@chewy/kib-overlays-styles/dist/styles/kib-sheet.css';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibInputText } from '@chewy/kib-fields-react';
import { KibAlert } from '@chewy/kib-messaging-react';
import { useMutation, useQueryClient } from 'react-query';
import FetchError from 'types/http';
import {
  CLINIC_RESULT_WARNING_THRESHOLD,
  CLINIC_NAME_MINIMUM,
  CLINIC_NAME_MAX_ENTRY_LENGTH,
} from '../../../api/constant';
import { usePetForm } from '../PetFormContext/PetFormContext';
import {
  assignVetClinicApi,
  findClinicsPaginated,
  getPetClinics,
  handleVetClinicError,
} from '../../../api/vetClinicApi';
import { scrollBody } from '../../../util/scrollBody';
import { scrollDocumentToVet } from '../util/scrollDocumentToVet';
import { isValidClinicInput } from './vetUtils/isValidClinicInput';
import {
  messageMinimumCharacters,
  addNewClinicText,
  clinicSearchLabel,
  dontSeeClinicText,
  narrowSearchText,
  noResultsText,
  noResultsTitle,
  searchText,
  searchVetText,
  vetSearchCriteriaText,
  narrowSearchTitle,
  alreadyAssociatedText,
} from './vetContent';
import { Clinic, Clinics } from './vetClinicResponse';
import VetClinicCard from './VetClinicCard';

const AddVetSheet = ({
  clinics,
  setClinics,
}: {
  clinics: Clinics;
  setClinics: React.Dispatch<React.SetStateAction<Clinics>>;
}) => {
  const {
    showAddVetSheet,
    prePopulatedSearchResults,
    setPrePopulatedSearchResults,
    triggerSearchOnload,
    setTriggerSearchOnload,
    setShowAddVetSheet,
    setShowOnboardingVetSheet,
    setDisplayAssignErrorSnackbar,
    clinicSearchValue,
    setClinicSearchValue,
  } = usePetForm();

  const [clinicResultsLoading, setClinicResultsLoading] = useState(false);
  const [clinicSearchResults, setClinicSearchResults] = useState<Array<Clinic>>(
    prePopulatedSearchResults,
  );
  const [searchClicked, setSearchClicked] = useState<Boolean>(triggerSearchOnload);
  const [showSearchValidations, setShowSearchValidations] = useState<Boolean>(false);
  const [vetSearchErrors, setVetSearchErrors] = useState<Array<string>>([]);
  const [displaySearchErrorAlert, setDisplaySearchErrorAlert] = useState<boolean>(false);
  const [alreadyAssociated, setAlreadyAssociated] = useState<boolean>(false);
  const [selectedClinic, setSelectedClinic] = useState<Clinic>();

  const errorAlertText = 'You can try to refresh the page or try again later.';
  const errorAlertTitle = "We're having trouble fetching clinics.";
  const handleSearchClinicsClick = () => {
    if (clinicSearchValue.trim().length < CLINIC_NAME_MINIMUM) {
      setVetSearchErrors([messageMinimumCharacters]);
    } else {
      vetSearchErrors && setVetSearchErrors([]);
      handleGetSearchResults();
    }
    setShowSearchValidations(true);
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    if (clinicSearchResults.length === 0) {
      handleGetSearchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddVetSheet]);

  const handleGetSearchResults = () => {
    async function getSearchResults() {
      if (vetSearchErrors.length === 0 && clinicSearchValue) {
        try {
          setClinicResultsLoading(true);
          const vetClinicsResults = await findClinicsPaginated.get(clinicSearchValue);
          setClinicSearchResults(vetClinicsResults);
        } catch (error) {
          setDisplaySearchErrorAlert(true);
          handleVetClinicError(error, 'searchClinics');
          // Error handling to be added with https://chewyinc.atlassian.net/browse/PPF-974
        } finally {
          setSearchClicked(true);
          setClinicResultsLoading(false);
        }
      }
    }
    getSearchResults();
  };

  const resetErrors = () => {
    showSearchValidations && setShowSearchValidations(false);
    vetSearchErrors.length > 0 && setVetSearchErrors([]);
    displaySearchErrorAlert && setDisplaySearchErrorAlert(false);
  };

  const resetSearch = () => {
    setClinicSearchValue('');
    setClinicSearchResults([]);
    setPrePopulatedSearchResults([]);
    setTriggerSearchOnload(false);
    setSearchClicked(false);
    resetErrors();
  };

  const invalidateVetQuery = () => {
    const invalidQueries = [queryClient.removeQueries(getPetClinics.key)];
    return Promise.all(invalidQueries);
  };

  const assignClinic = async (clinic: Clinic) => {
    if (clinicSearchValue) {
      try {
        await assignVetClinicApi.assign(clinic.id);
        invalidateVetQuery();
        return clinic;
      } catch (error) {
        setDisplayAssignErrorSnackbar(true);
        handleVetClinicError(error, 'assignClinic');
      }
    } else {
      setShowAddVetSheet(false);
      resetSearch();
    }
  };

  const handleSelectClinic = (clinic: Clinic) => {
    setAlreadyAssociated(false);
    setSelectedClinic(clinic);
    if (!!handleCheckExisting(clinic)) {
      setAlreadyAssociated(true);
    } else {
      assignMutate(clinic);
    }
  };

  const { mutate: assignMutate } = useMutation(assignClinic, {
    onSuccess: (clinic) => {
      if (clinic) {
        const updatedClinicList = clinics.concat([clinic]);
        setClinics(updatedClinicList);
      }
      setShowAddVetSheet(false);
      resetSearch();
      scrollBody(true);
      scrollDocumentToVet();
    },
    // Error handling to be added with https://chewyinc.atlassian.net/browse/PPF-974
    onError: (error: FetchError) => {
      throw new Error(`Failed to assign clinic. Status code: ${error.status}. Error: ${error}`);
    },
  });

  const handleCheckExisting = (clinic: Clinic) => {
    const matches = clinics.filter((e) => e.id === clinic.id).length;
    return matches > 0 ? true : false;
  };
  return (
    <KibSheet
      open={showAddVetSheet}
      onClose={() => {
        setShowAddVetSheet(false);
        resetSearch();
        scrollBody(true);
        scrollDocumentToVet();
      }}
      initialFocus="#sheet-initialFocus"
      title={searchVetText}
      size="large"
      data-testid="search-clinic-sheet"
    >
      <div className="pet-form-vet-search__sheet" data-testid="pet-form-vet-search-sheet">
        <form id="vet-clinic-search">
          <div className="kib-margin__bottom">{vetSearchCriteriaText}</div>
          <div className="pet-form-vet-search__buttons">
            <KibInputText
              data-testid="pet-form-vet-searchbar"
              className="pet-form-vet-search__searchbar"
              id="sheet-initialFocus"
              label={clinicSearchLabel}
              value={clinicSearchValue}
              required={true}
              onChange={(event) => {
                vetSearchErrors.length > 0 && resetErrors();
                const clinicInput = event.target.value;
                if (
                  isValidClinicInput(clinicInput) &&
                  clinicInput.length <= CLINIC_NAME_MAX_ENTRY_LENGTH
                ) {
                  setClinicSearchValue(clinicInput);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  resetErrors();
                  handleSearchClinicsClick();
                }
              }}
              invalid={showSearchValidations && vetSearchErrors.length > 0}
              messagesContent={showSearchValidations && vetSearchErrors}
            />
            <KibButtonNew
              className="pet-form-vet-search__button"
              aria-label="search vet"
              data-testid="search-vet-button"
              onClick={() => {
                resetErrors();
                handleSearchClinicsClick();
              }}
              disabled={clinicResultsLoading}
            >
              {searchText}
            </KibButtonNew>
          </div>
        </form>
        {displaySearchErrorAlert && (
          <KibAlert
            severity="error"
            title={errorAlertTitle}
            actions={() => (
              <button
                data-testid="pet-form-vet-search-none-found-refresh"
                className="pet-form-vet-search__refresh"
                onClick={() => {
                  setShowAddVetSheet(false);
                  resetSearch();
                  setShowAddVetSheet(true);
                }}
              >
                Refresh
              </button>
            )}
            data-testid="pet-form-vet-search-none-found"
          >
            {errorAlertText}
          </KibAlert>
        )}
        {searchClicked && !displaySearchErrorAlert && (
          <>
            {!clinicResultsLoading &&
              clinicSearchResults &&
              clinicSearchResults.length > CLINIC_RESULT_WARNING_THRESHOLD && (
                <KibAlert
                  severity="caution"
                  title={narrowSearchTitle}
                  data-testid="pet-form-vet-search-narrow-search"
                >
                  {narrowSearchText}
                </KibAlert>
              )}
            {!clinicResultsLoading && clinicSearchResults && clinicSearchResults.length === 0 && (
              <KibAlert
                severity="error"
                title={noResultsTitle}
                data-testid="pet-form-vet-search-none-found"
              >
                {noResultsText}
              </KibAlert>
            )}
            {!clinicResultsLoading && (
              <div
                className="pet-form-vet-search__subtitle"
                data-testid="pet-form-vet-search-subtitle"
              >
                <span>{dontSeeClinicText}</span>
                <button
                  data-testid="pet-form-vet-search-addNewBtn"
                  type="button"
                  onClick={() => {
                    setShowAddVetSheet(false);
                    scrollBody(true);
                    resetSearch();
                    setShowOnboardingVetSheet(true);
                  }}
                  className="pet-form-vet-search__addNewVet"
                >
                  {addNewClinicText}
                </button>
              </div>
            )}
            {clinicSearchResults && clinicSearchResults.length > 0 && (
              <div role="radiogroup" data-testid="search-clinics-radiogroup">
                {clinicSearchResults.map((clinic, index) => (
                  <>
                    <KibSelectionCard
                      key={index}
                      data-testid="pet-form-vet-search-card"
                      name="clinicOptions"
                      label={<VetClinicCard clinic={clinic} isSearch={true} />}
                      onChange={() => {
                        handleSelectClinic(clinic);
                      }}
                      selectedValue={selectedClinic}
                      value={clinic}
                    />
                    {!!alreadyAssociated && clinic === selectedClinic && (
                      <KibAlert
                        data-testid="pet-form-vet-search-existing-alert"
                        className="pet-form-vet-search__alert"
                      >
                        {alreadyAssociatedText}
                      </KibAlert>
                    )}
                  </>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </KibSheet>
  );
};

export default AddVetSheet;
